// Konuşmanın duygusu → avatar rengi, Plutchik'in duygu çarkıyla (D-666).
//
// Batuhan: "konuşmanın içeriğine göre bir renge dönüşmeye başlasın, kişinin
// profili eğer yoksa, belirlenen renk." Kalpler ile öfke arasındaki açık
// soruya: "Yavaştan başlayabiliriz. Robert Plutchik üzerine gidebiliriz."
//
// İlk dilim bilerek dar:
// - Sinyal yalnız emoji ve tepkiler; her dilde aynı çalışırlar, metin
//   çözümlemesi yok.
// - Sekiz temel duygu ve Plutchik'in "sevgi" ikilisi (neşe + güven). Kalpler
//   sevgidir, kırmızı değil: çarkta kırmızı öfkedir. Kırık kalp üzüntüdür.
// - Nesneler (🍎, 🌊) sayılmaz; duygu taşımazlar.
// - En baskın duygu kazanır; renkler karışmaz. Kırmızı ile mavinin ortası mor
//   olurdu ve mor çarkta tiksintidir: karıştırmak yanlış bir duygu söylerdi.
// - Yeni mesaj eskisinden ağır basar; renk konuşmayla birlikte kayar.
//
// Hesap YALNIZ bu cihazda yapılır; sonuç (tek bir duygu adı) cihazda saklanır,
// sunucuya hiçbir şey gitmez.

export type Emotion =
  | 'joy' | 'trust' | 'fear' | 'surprise' | 'sadness' | 'disgust' | 'anger' | 'anticipation' | 'love';

/**
 * Zemin ve baş harf rengi. Tonlar uygulamanın isim paletinden (Avatar) seçildi,
 * yani yeni bir renk dili açılmıyor; her ikilide baş harf zeminden en az 4,5:1
 * ayrışıyor. "Sevgi" çarkta neşe (sarı) ile güvenin (yeşil) arasındadır.
 */
export const EMOTION_TINT: Record<Emotion, readonly [string, string]> = {
  joy: ['#F9C74F', '#000'],
  trust: ['#90BE6D', '#000'],
  love: ['#C4C25E', '#000'],
  fear: ['#43AA8B', '#000'],
  surprise: ['#4D908E', '#000'],
  sadness: ['#277DA1', '#fff'],
  disgust: ['#9C6ADE', '#000'],
  anger: ['#F94144', '#000'],
  anticipation: ['#F3722C', '#000'],
};

const TABLE: Record<Emotion, string[]> = {
  joy: ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '🙂', '😉', '😋', '😛', '😜', '🤪', '😝', '🥳', '😎', '🎉', '🥲'],
  trust: ['👍', '🙏', '🤝', '👌', '🤗', '😇', '🫡', '💯', '✅'],
  love: ['❤', '🧡', '💛', '💚', '💙', '💜', '🤍', '🤎', '💕', '💖', '💗', '💓', '💞', '💘', '💝', '❣', '😍', '🥰', '😘', '😻', '💋', '🫶', '😚', '😙'],
  fear: ['😨', '😰', '😱', '😟', '😧', '🫣', '😬'],
  surprise: ['😮', '😯', '😲', '😳', '🤯', '😦', '🙀', '‼'],
  sadness: ['😢', '😭', '😞', '😔', '☹', '🙁', '😿', '🥺', '😥', '😓', '💔'],
  disgust: ['🤢', '🤮', '😒', '🙄', '😖', '😣', '👎'],
  anger: ['😠', '😡', '🤬', '👿', '💢', '😤'],
  anticipation: ['🤔', '🤞', '👀', '⏳', '🧐', '🔜'],
};

const EMOJI_TO_EMOTION = new Map<string, Emotion>();
for (const [emotion, list] of Object.entries(TABLE) as [Emotion, string[]][]) {
  for (const emoji of list) EMOJI_TO_EMOTION.set(emoji, emotion);
}

// En uzun anahtar önce: çok kod noktalı bir emoji, içindeki tek kod noktalı
// bir emojiyle karışmasın.
const PATTERN = new RegExp(
  [...EMOJI_TO_EMOTION.keys()]
    .sort((a, b) => b.length - a.length)
    .map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|'),
  'gu',
);

/** Görünüm seçicisi (U+FE0F) ve ten rengi değiştiricileri anlamı değiştirmez. */
function normalize(text: string): string {
  return text.replace(/[️\u{1F3FB}-\u{1F3FF}]/gu, '');
}

/** Metindeki duygu emojileri, sırasıyla. */
export function emotionsIn(text: string): Emotion[] {
  const out: Emotion[] = [];
  for (const match of normalize(text).matchAll(PATTERN)) {
    const emotion = EMOJI_TO_EMOTION.get(match[0]);
    if (emotion) out.push(emotion);
  }
  return out;
}

export type MoodMessage = {
  type: string;
  content: string;
  reactions?: { emoji: string; count: number }[];
};

// Yarılanma 20 mesaj: 20 mesaj önceki bir emoji, şimdikinin yarısı kadar sayılır.
const HALF_LIFE = 20;
const WINDOW = 200;
// En az bir emojinin yakın zamanda geçmiş olması gerekir; yoksa isim rengi kalır.
const MIN_SCORE = 0.5;
const ORDER: Emotion[] = ['love', 'joy', 'trust', 'anticipation', 'surprise', 'fear', 'sadness', 'disgust', 'anger'];

/**
 * Konuşmanın baskın duygusu; sinyal yoksa null. Mesajlar EN YENİ ÖNCE gelir.
 * Yalnız metin mesajları okunur (şifreli olanlar çözülünce metin olur);
 * tepkiler ait oldukları mesajın ağırlığıyla sayılır.
 */
export function conversationMood(messagesNewestFirst: MoodMessage[]): Emotion | null {
  const score = new Map<Emotion, number>();
  let index = 0;
  for (const message of messagesNewestFirst) {
    if (index >= WINDOW) break;
    if (message.type === 'call' || message.type === 'identity_change') continue;
    const weight = Math.pow(0.5, index / HALF_LIFE);
    index += 1;
    const found = message.type === 'text' ? emotionsIn(message.content) : [];
    for (const reaction of message.reactions ?? []) {
      const emotion = emotionsIn(reaction.emoji)[0];
      if (emotion) for (let i = 0; i < Math.max(1, reaction.count); i += 1) found.push(emotion);
    }
    for (const emotion of found) score.set(emotion, (score.get(emotion) ?? 0) + weight);
  }
  let best: Emotion | null = null;
  let bestScore = 0;
  for (const emotion of ORDER) {
    const value = score.get(emotion) ?? 0;
    if (value > bestScore) {
      best = emotion;
      bestScore = value;
    }
  }
  return bestScore >= MIN_SCORE ? best : null;
}

export function isEmotion(value: unknown): value is Emotion {
  return typeof value === 'string' && value in EMOTION_TINT;
}
